import * as React from "react";
import { processTable } from "../table/processTable";
import { useQuery, } from "@apollo/client";
import { Filter } from "../component/filter";
import { useFilter } from "../component/useFilter";
import { useSort } from "../component/useSort";
import { useTableSettings } from "../component/useTableSettings";
import { Loading } from "../component/Loading";
import { ChevronDown, ChevronUp, Settings, } from "@volkswagen-onehub/icons-core";
import { StyledTh, StyledTable, StyledTd } from "../component/Table";
import { Container, CTA, Text, LayerWrapper, ShimLayer, FocusLayer, TokenTextAppearance, Checkbox, Select, } from "@volkswagen-onehub/components-core";
import { useCustomFilters } from "./useCustomFilters";
export function ExtendedTable(props) {
    const { getData, query, meta, filters, autoFetchMore, variables = {}, } = props;
    const { loading, error, data, fetchMore } = useQuery(query, { variables });
    const { filterFn, filterValue } = useFilter();
    const { ascending, sortIndex, toggleSort } = useSort();
    const { settingsOpen, setSettingsOpen, updateVisibleColumn, visibleColumns } = useTableSettings(meta);
    const [getFilterOption, setFilterOption] = useCustomFilters(filters || []);
    const dataLength = React.useRef(0);
    const autoFetchMoreFn = React.useCallback((data) => {
        if (!data) {
            return undefined;
        }
        const lastFetchCount = data.length - dataLength.current;
        if (lastFetchCount === autoFetchMore?.limit) {
            dataLength.current = data.length;
            return { limit: autoFetchMore?.limit, start: data.length };
        }
    }, [dataLength, autoFetchMore?.limit]);
    React.useEffect(() => {
        if (autoFetchMore && data) {
            const variables = autoFetchMoreFn(getData(data));
            if (variables) {
                fetchMore({
                    variables,
                });
            }
        }
    }, [data, autoFetchMore, autoFetchMoreFn, getData, fetchMore]);
    const filterFns = filters?.map((f) => (data) => {
        const filterValue = getFilterOption(f.label);
        return f.filterFn(filterValue, data);
    }) || [];
    return (React.createElement(Loading, { loading: loading, error: error, render: () => {
            const rows = processTable(getData(data), meta, 0, sortIndex, ascending, filterValue, visibleColumns, filterFns);
            const rowCount = getRowCount(rows);
            const gridTemplateColumns = `200px ${filterFns.map(() => "110px").join(" ")} 34px`;
            return (React.createElement(React.Fragment, null,
                React.createElement(Container, { gutter: "dynamic0200", wrap: "always" },
                    React.createElement("div", null,
                        React.createElement("div", { style: {
                                display: "grid",
                                gridTemplateColumns,
                                alignItems: "center",
                                columnGap: "20px",
                            } },
                            React.createElement(Filter, { filter: filterFn }),
                            filters?.map((filter) => {
                                if (filter.type == "boolean") {
                                    return (React.createElement(Checkbox, { key: filter.label, value: "true", label: filter.label, checked: getFilterOption(filter.label), onChange: () => {
                                            setFilterOption(filter.label, !getFilterOption(filter.label));
                                        } }));
                                }
                                else if (filter.type === "select") {
                                    return (React.createElement(Select, { key: filter.label, label: filter.label, defaultValue: filter.options[0].value, onChange: (e) => {
                                            setFilterOption(filter.label, filter.options.find((o) => o.value === e.currentTarget.value)?.value || "");
                                        } }, filter.options.map((o) => {
                                        return (React.createElement("option", { key: `opt_${o.value}`, label: o.label, value: o.value }));
                                    })));
                                }
                            }),
                            React.createElement(CTA, { tag: "button", emphasis: "none", icon: React.createElement(Settings, null), onClick: () => setSettingsOpen(true) })),
                        React.createElement(Text, { appearance: TokenTextAppearance.copy100 },
                            "total: ",
                            rowCount)),
                    React.createElement(StyledTable, null,
                        React.createElement("thead", null,
                            React.createElement("tr", null, meta.columns
                                .filter((_c, idx) => visibleColumns[idx])
                                .map((c, idx) => {
                                const enabled = sortIndex === idx;
                                const emphasis = enabled ? "primary" : "tertiary";
                                const icon = ascending ? (React.createElement(ChevronUp, { variant: "small" })) : (React.createElement(ChevronDown, { variant: "small" }));
                                return (React.createElement(StyledTh, { key: idx },
                                    React.createElement("div", { style: {
                                            textAlign: "center",
                                            ...(c.sortable && {
                                                display: "grid",
                                                gridTemplateColumns: "repeat(2, 1fr)",
                                                alignItems: "center",
                                                columnGap: "10px",
                                            }),
                                        } },
                                        React.createElement(Text, null, c.header.label),
                                        c.sortable && (React.createElement(CTA, { emphasis: emphasis, tag: "button", icon: icon, onClick: () => toggleSort(idx) })))));
                            }))),
                        React.createElement("tbody", null, rows.map((row, ridx) => {
                            return (React.createElement("tr", { key: ridx }, row.cells.map((cell, cidx) => {
                                return (React.createElement(StyledTd, { key: cidx, rowSpan: cell.span, 
                                    // Dont break if number or action link
                                    breakWord: typeof cell.data !== "string" ||
                                        !/\d/.test(cell.data) }, cell.data));
                            })));
                        })))),
                React.createElement(LayerWrapper, { contentCentered: true }, settingsOpen
                    ? [
                        React.createElement(ShimLayer, { key: "shim" }),
                        React.createElement(FocusLayer, { key: "focus", active: true, size: "A" },
                            React.createElement(Container, { gutter: "static300", padding: "grid001", wrap: "always" },
                                React.createElement(Text, { bold: true, appearance: TokenTextAppearance.headline300 }, "Column visibility"),
                                meta.columns.map((c, idx) => {
                                    const visible = visibleColumns[idx];
                                    return (React.createElement(Checkbox, { key: "checkbox", value: String(idx), checked: visible, onClick: () => {
                                            updateVisibleColumn(idx, !visible);
                                        }, label: c.header.label }));
                                }),
                                React.createElement(CTA, { onClick: () => setSettingsOpen(false), tag: "button", emphasis: "secondary" }, "Close"))),
                    ]
                    : undefined)));
        } }));
}
function getRowCount(rows) {
    return rows
        .map((r) => (r.cells.length > 0 ? r.cells[0].span : 1))
        .reduce((agg, span) => {
        if (agg.skip === 0) {
            return { count: agg.count + 1, skip: span - 1 };
        }
        else {
            return { count: agg.count, skip: agg.skip - 1 };
        }
    }, { count: 0, skip: 0 }).count;
}
